import React, { useMemo, useState } from 'react';

import { Button } from '@appcharge/shared-ui';
import useLocalization from 'api/useLocalization';
import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { LanguageDataParams } from 'views/Localization/components/types';
import {
  getCountriesOptions,
  getSupportedLanguageCodes,
  handleRenderCountriesList
} from 'views/Localization/utils';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

import CountrySelect from '../CountrySelect/CountrySelect';

import { AddLanguageFormProps } from './AddLanguageForm.types';
import { addNewLanguageSchema } from './validation-schema';

import './style.scss';

const AddLanguageForm = ({
  onSave,
}: AddLanguageFormProps) => {
  const { getAddedLanguagesList } = useLocalization();

  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const countriesOptions = useMemo(() => getCountriesOptions(), []);
  const languageCodesOptions = useMemo(() => getSupportedLanguageCodes(), []);

  const addedLanguagesList = getAddedLanguagesList?.data?.map((item: LanguageDataParams) => item?.language?.toLowerCase());
  const existingLanguageCodesList = getAddedLanguagesList?.data?.map((item: LanguageDataParams) => item?.isoCode?.toLowerCase());

  const handleSelectCountry = (countryValue: string) => {
    const newSelectedValues = selectedCountries.includes(countryValue)
      ? selectedCountries.filter((c) => c !== countryValue)
      : [...selectedCountries, countryValue];
    setFieldValue('country', newSelectedValues, true);
    setSelectedCountries(newSelectedValues);
  };

  const handleBulkCountryChange = (countries: string[]) => {
    setFieldValue('country', countries, true);
    setSelectedCountries(countries);
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    submitForm,
    isValid,
    setFieldValue,
    setFieldTouched
  } = useFormik({
    initialValues: {
      language: '',
      country: [],
      isEnabled: false,
      isoCode: ''
    },
    validationSchema: addNewLanguageSchema(addedLanguagesList, existingLanguageCodesList),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values, { resetForm }) => {
      const parsedLanguageData = values.country.map((country) => ({
        language: values.language,
        country:
          (countriesOptions.find((option) => option.value === country)
            ?.key as string) ?? '',
        isEnabled: values.isEnabled,
        isoCode: values.isoCode
      }));

      const newLanguagesList = getAddedLanguagesList?.data
        ? [...getAddedLanguagesList.data, ...parsedLanguageData]
        : parsedLanguageData;
      onSave(newLanguagesList);
      setSelectedCountries([]);
      resetForm();
    }
  });

  const languageCodeOptions = useMemo(() =>
    languageCodesOptions.map(opt => ({
      value: opt.value as string,
      label: opt.content as string,
      content: opt.content as string
    })),
    [languageCodesOptions]
  );

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="flex gap-5">
        <div className="w-[282px]">
          <Input
            label="Display Name"
            isRequired
            name="language"
            value={values.language}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Language Display Name"
            state={!!errors.language ? 'error' : 'default'}
            description={(errors.language as string) || ''}
            className="w-full language-input"
          />
        </div>
        <div className="w-[199px]">
          <InputSelectDropdown
            key={values.isoCode || 'empty'}
            label="Language Code"
            options={languageCodeOptions}
            defaultSelected={languageCodeOptions.find(opt => opt.value === values.isoCode)}
            searchPlaceholder="Search..."
            placeholder="Select"
            onOptionChange={(value) => {
              setFieldValue('isoCode', value, true);
              setFieldTouched('isoCode', true, false);
            }}
            description={(errors.isoCode && touched.isoCode) ? errors.isoCode : ''}
            state={(errors.isoCode && touched.isoCode) ? 'error' : 'default'}
            isRequired
          />
        </div>
        <div className="w-[199px]">
          {/*  This is TODO: Task ACDEV-18482 - will be changed with reusable component from shared-ui */}
          <CountrySelect
            label="* Country"
            items={countriesOptions.map(opt => ({
              value: opt.value as string,
              label: opt.content as string,
              renderFunction: opt.renderFunction
            }))}
            selectedItems={selectedCountries}
            onChange={handleSelectCountry}
            onBulkChange={handleBulkCountryChange}
            searchPlaceholder="Search..."
            placeholder="Select"
            renderValue={handleRenderCountriesList}
          />
        </div>
        <div className="mt-7">
          <Button
            className="bg-base-accent"
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              submitForm();
            }}
            disabled={!isValid}
            type="button"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add New
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AddLanguageForm;
